import { useEffect, useState } from 'react';
import * as civilLitigationService from '../../../services/civilLitigationCaseRequests';
import type { LookupItem } from '../../../mocks/criminalMasterData';
import { courtTypeQueryParam } from './civilLitigationHelpers';

export type CivilLitigationLookupScope = 'step1' | 'step2' | 'step3' | 'step4' | 'step5' | 'review';

type LookupBundle = {
  provinces: LookupItem[];
  districts: LookupItem[];
  courtTypes: LookupItem[];
  courts: LookupItem[];
  urgencyLevels: LookupItem[];
  deliveryMethods: LookupItem[];
  corporateInstitutes: LookupItem[];
  nextSteps: LookupItem[];
};

const EMPTY: LookupBundle = {
  provinces: [],
  districts: [],
  courtTypes: [],
  courts: [],
  urgencyLevels: [],
  deliveryMethods: [],
  corporateInstitutes: [],
  nextSteps: [],
};

let sharedLookups: LookupBundle = { ...EMPTY };

export function useCivilLitigationLookups(
  scope: CivilLitigationLookupScope,
  provinceId?: string,
  districtId?: string,
  courtTypeId?: string,
) {
  const [data, setData] = useState<LookupBundle>(sharedLookups);
  const [loading, setLoading] = useState(scope !== 'step4' && scope !== 'step5');
  const [courtsLoading, setCourtsLoading] = useState(false);

  useEffect(() => {
    if (scope === 'step5') {
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(scope !== 'step4');

    const load = async (): Promise<Partial<LookupBundle>> => {
      if (scope === 'step4') {
        const del = await civilLitigationService.getDeliveryMethods();
        return { deliveryMethods: del.success ? del.data : [] };
      }
      if (scope === 'step1') {
        const [prov, corp, courtTypes] = await Promise.all([
          civilLitigationService.getProvinces(),
          civilLitigationService.getCorporateInstitutes(),
          civilLitigationService.getLitigationCourtTypes(),
        ]);
        return {
          provinces: prov.success ? prov.data : [],
          corporateInstitutes: corp.success ? corp.data : [],
          courtTypes: courtTypes.success ? courtTypes.data : [],
        };
      }
      if (scope === 'step2') {
        return {};
      }
      if (scope === 'step3') {
        const [urg, next] = await Promise.all([
          civilLitigationService.getUrgencyLevels(),
          civilLitigationService.getCivilLitigationNextSteps(),
        ]);
        return {
          urgencyLevels: urg.success ? urg.data : [],
          nextSteps: next.success ? next.data : [],
        };
      }
      const [prov, urg, del, corp, courtTypes, next] = await Promise.all([
        civilLitigationService.getProvinces(),
        civilLitigationService.getUrgencyLevels(),
        civilLitigationService.getDeliveryMethods(),
        civilLitigationService.getCorporateInstitutes(),
        civilLitigationService.getLitigationCourtTypes(),
        civilLitigationService.getCivilLitigationNextSteps(),
      ]);
      return {
        provinces: prov.success ? prov.data : [],
        urgencyLevels: urg.success ? urg.data : [],
        deliveryMethods: del.success ? del.data : [],
        corporateInstitutes: corp.success ? corp.data : [],
        courtTypes: courtTypes.success ? courtTypes.data : [],
        nextSteps: next.success ? next.data : [],
      };
    };

    void load().then((partial) => {
      if (!mounted) return;
      sharedLookups = { ...sharedLookups, ...partial };
      setData({ ...sharedLookups });
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [scope]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    if (!provinceId) {
      setData((d) => ({ ...d, districts: [] }));
      return;
    }

    let mounted = true;
    void civilLitigationService.getDistricts(provinceId).then((r) => {
      if (!mounted) return;
      const districts = r.success ? r.data : [];
      sharedLookups = { ...sharedLookups, districts };
      setData((d) => ({ ...d, districts }));
    });
    return () => {
      mounted = false;
    };
  }, [scope, provinceId]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    const courtType = courtTypeQueryParam(data.courtTypes, courtTypeId ?? '');
    if (!courtType) {
      setCourtsLoading(false);
      setData((d) => ({ ...d, courts: [] }));
      return;
    }

    let mounted = true;
    setCourtsLoading(true);
    void civilLitigationService.getLitigationCourts(courtType, districtId, provinceId).then((r) => {
      if (!mounted) return;
      const courts = r.success ? r.data : [];
      sharedLookups = { ...sharedLookups, courts };
      setData((d) => ({ ...d, courts }));
      setCourtsLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [scope, courtTypeId, districtId, provinceId, data.courtTypes]);

  return {
    loading,
    provinces: data.provinces,
    districts: data.districts,
    courtTypes: data.courtTypes,
    courts: data.courts,
    courtsLoading,
    urgencyLevels: data.urgencyLevels,
    deliveryMethods: data.deliveryMethods,
    corporateInstitutes: data.corporateInstitutes,
    nextSteps: data.nextSteps,
  };
}
